import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnhancedCart } from '@/contexts/EnhancedCartContext';
import { useSettings, DeliveryLocation } from '@/contexts/SettingsContext';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { DeliveryMethodSelect } from '@/components/checkout/DeliveryMethodSelect';
import { DeliveryLocationSelect } from '@/components/checkout/DeliveryLocationSelect';
import { PaymentMethodSelect, PaymentMethod } from '@/components/checkout/PaymentMethodSelect';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ShoppingBag, Loader } from 'lucide-react';
import api from '@/utils/api';
import { getCartLineSubtotal, getEffectiveUnitPrice } from '@/utils/wholesalePricing';

export function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useEnhancedCart();
  const { checkoutOptions } = useSettings();
  const { currentStore, storeId } = useCurrentStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Customer information
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  
  // Checkout selections
  const [deliveryMethod, setDeliveryMethod] = useState<'delivery' | 'pickup'>('pickup');
  const [selectedLocation, setSelectedLocation] = useState<DeliveryLocation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [paymentDetails, setPaymentDetails] = useState<{
    proofOfPayment?: File;
    notes?: string;
  }>({});
  const [orderNotes, setOrderNotes] = useState('');
  
  // Calculate totals
  const subtotal = getTotal();
  const deliveryFee = selectedLocation?.price || 0;
  const total = subtotal + deliveryFee;
  
  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString('en-NG', {
      style: 'currency',
      currency: currentStore?.currency || 'NGN',
    });
  };
  
  const handleCustomerInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleDeliveryMethodSelect = (method: 'delivery' | 'pickup') => {
    setDeliveryMethod(method);
    if (method === 'pickup') {
      setSelectedLocation(null);
    }
  };
  
  const handleLocationSelect = (location: DeliveryLocation | null) => {
    setSelectedLocation(location);
  };
  
  const handlePaymentMethodSelect = (
    method: PaymentMethod, 
    details?: { proofOfPayment?: File, notes?: string }
  ) => {
    setPaymentMethod(method);
    if (details) {
      setPaymentDetails(details);
    }
  };
  
  const handleOrderNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOrderNotes(e.target.value);
  };
  
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (items.length === 0) {
      return;
    }
    
    // Basic form validation
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (deliveryMethod === 'delivery' && !selectedLocation) {
      alert('Please select a delivery location');
      return;
    }
    
    if (!paymentMethod) {
      alert('Please select a payment method');
      return;
    }
    
    // For bank transfer, proof of payment is required
    if (paymentMethod === 'bankTransfer' && !paymentDetails.proofOfPayment) {
      alert('Please upload proof of payment');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Create form data for file upload
      const formData = new FormData();
      
      // Add basic order info
      const orderData = {
        customer: customerInfo,
        items: items.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          variantName: item.variantName,
          variantAttributes: item.variantAttributes,
          quantity: item.quantity,
          price: getEffectiveUnitPrice(item.price, item.quantity, item.wholesale),
          options: item.options,
        })),
        delivery: {
          method: deliveryMethod,
          location: selectedLocation,
          fee: deliveryFee,
        },
        payment: {
          method: paymentMethod,
          notes: paymentDetails.notes || '',
        },
        subtotal,
        total,
        notes: orderNotes,
      };
      
      formData.append('orderData', JSON.stringify(orderData));
      
      // Add payment proof if available
      if (paymentDetails.proofOfPayment) {
        formData.append('paymentProof', paymentDetails.proofOfPayment);
      }
      
      // Submit order
      const response = await api.post('/api/orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Store-ID': storeId, // Add store ID header
        },
      });
      
      // Order completed successfully
      if (response.data?.success && response.data?.order) {
        // Clear cart
        clearCart();
        
        // Redirect to order confirmation
        navigate(`/order-confirmation/${response.data.order._id}`);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('There was an error processing your order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If cart is empty, redirect to home
  if (items.length === 0) {
    return (
      <div className="container max-w-5xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-muted-foreground mb-8">Add items to your cart to checkout</p>
          <Button onClick={() => navigate('/')}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Checkout</h1>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
              <CardDescription>Enter your contact and delivery details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleCustomerInfoChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={handleCustomerInfoChange}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleCustomerInfoChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={customerInfo.address}
                    onChange={handleCustomerInfoChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Delivery Options */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Options</CardTitle>
              <CardDescription>Choose how you want to receive your order</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <DeliveryMethodSelect onSelectMethod={handleDeliveryMethodSelect} />
              
              {deliveryMethod === 'delivery' && (
                <DeliveryLocationSelect onSelectLocation={handleLocationSelect} />
              )}
            </CardContent>
          </Card>
          
          {/* Payment Options */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Method</CardTitle>
              <CardDescription>Select how you want to pay for your order</CardDescription>
            </CardHeader>
            <CardContent>
              <PaymentMethodSelect onSelectMethod={handlePaymentMethodSelect} />
            </CardContent>
          </Card>
          
          {/* Order Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Order Notes</CardTitle>
              <CardDescription>Add any additional information about your order</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={orderNotes}
                onChange={handleOrderNotesChange}
                placeholder="Any special instructions for your order..."
                className="resize-none"
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Order Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
              <CardDescription>Review your order details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Items ({items.length})</h3>
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={`${item.productId}-${JSON.stringify(item.options)}`} className="flex gap-3">
                      {item.image && (
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-16 h-16 object-cover rounded" 
                        />
                      )}
                                              <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          {item.variantName && (
                            <div className="text-xs text-muted-foreground">
                              Variant: {item.variantName}
                            </div>
                          )}
                          {Object.entries(item.options || {}).map(([key, value]) => (
                            <div key={key} className="text-xs text-muted-foreground">
                              {key}: {value}
                            </div>
                          ))}
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-sm text-muted-foreground">
                              Qty: {item.quantity}
                            </span>
                            <span>{formatPrice(getCartLineSubtotal(item))}</span>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span>
                    {deliveryMethod === 'delivery' && selectedLocation
                      ? formatPrice(deliveryFee)
                      : 'Free'}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                size="lg"
                onClick={handleSubmitOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Place Order'
                )}
              </Button>
            </CardFooter>
          </Card>
          
          <div className="text-sm text-muted-foreground">
            <p>
              By placing your order, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 